// React
import React, { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { TenantUserRow } from "services/api/tenants/types";
import { searchTenantUsersAPI } from "services/api/tenants/api";
import { deleteUserAPI } from "services/api/users/api";
import { initialDataWithPagination } from "services/api/consts";

// UI
import { Button } from "ui/components/Button/Button";
import { Dialog } from "ui/components/Dialog";
import { ZPosition } from "ui/components/Modal/types";
import { TableSearch } from "ui/components/Table/TableSearch";

// Lokalne komponente
import UserEditCard from "ui/modules/users/pages/UserDetailPage/components/UserDetailCard/components/UserEditCard/UserEditCard";

// Tipovi i konstante
import { TENANT_USER_COLUMNS } from "./consts";
import { TenantUserStepProps } from "./types";

const TenantUserStep: React.FC<TenantUserStepProps> = ({
  formValues: parentFormValues,
  onCountChange,
}) => {
  const tenantId = parentFormValues.id;

  const { t } = useTranslate();

  const [users, setUsers] = useState<TenantUserRow[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [userId, setUserId] = useState<string | null>(null);
  const [showUserModal, setShowUserModal] = useState<boolean>(false);

  const [deleteId, setDeleteId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const fetchUsers = useCallback(async () => {
    if (!tenantId) return;

    setIsLoading(true);

    try {
      const result = await searchTenantUsersAPI(
        tenantId,
        initialDataWithPagination.pagination
      );

      setUsers(result.data);
      onCountChange?.(result.data.length);
    } catch {
      setUsers([]);
      onCountChange?.(0);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, onCountChange]);

  useEffect(() => {
    if (tenantId) fetchUsers();
  }, [tenantId, fetchUsers]);

  /*----------------------------------------------------------------
      UNOS I IZMJENA
  ----------------------------------------------------------------*/

  const handleAddUser = useCallback(() => {
    setUserId(null);
    setShowUserModal(true);
  }, []);

  const handleRowClick = useCallback((id: string) => {
    setUserId(id);
    setShowUserModal(true);
  }, []);

  const handleUserSuccess = useCallback(() => {
    setShowUserModal(false);
    setUserId(null);
    fetchUsers();
  }, [fetchUsers]);

  const handleUserClose = useCallback(() => {
    setShowUserModal(false);
    setUserId(null);
  }, []);

  /*----------------------------------------------------------------
      BRISANJE
  ----------------------------------------------------------------*/

  // Klik na koš ne smije otvoriti i uređivanje retka ispod njega.
  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>, id: string) => {
      event.stopPropagation();
      event.preventDefault();

      setDeleteId(id);
      setOpenDialog(true);
    },
    []
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteId) return;

    try {
      await deleteUserAPI(deleteId, t);
      await fetchUsers();
    } finally {
      setOpenDialog(false);
      setDeleteId("");
    }
  }, [deleteId, t, fetchUsers]);

  const handleDeleteCancel = useCallback(() => {
    setOpenDialog(false);
    setDeleteId("");
  }, []);

  return (
    <div className="flex items-start justify-center px-4 mt-8 xl:mt-16 min-h-[60vh] xl:min-h-[600px]">
      <div className="w-full max-w-4xl">
        <h1 className="text-2xl font-bold text-slate-900 text-center mb-1">Korisnici</h1>

        <p className="text-center text-sm text-slate-600 mb-6 max-w-2xl mx-auto">
          Dodajte korisnike koji pripadaju ovom tenantu. Klikom na redak otvara se izmjena.
        </p>

        {/* Uređivanje ide klikom na redak; prazno stanje nudi samo unos. */}
        {!isLoading && users.length === 0 ? (
          <div className="max-w-md mx-auto">
            <Button
              variant="primary"
              size="full"
              children="Dodaj korisnika"
              startIcon="/media/icons/plus.svg"
              onClick={handleAddUser}
            />
          </div>
        ) : (
          <>
            <div className="flex justify-end mb-3">
              <Button
                variant="primary"
                children="Dodaj korisnika"
                startIcon="/media/icons/plus.svg"
                onClick={handleAddUser}
              />
            </div>

            <TableSearch
              columns={TENANT_USER_COLUMNS(handleDeleteClick)}
              data={users}
              selectableRows={false}
              onRowClick={handleRowClick}
              isLoading={isLoading}
              disablePagination
            />
          </>
        )}
      </div>

      {/*
        Otvara se nad modalom tenanta, pa mu treba viši sloj: modal s uvodnom plohom Modal
        prisilno stavlja na dno, gdje je i sam čarobnjak tenanta.
      */}
      <UserEditCard
        userId={userId}
        tenantId={tenantId}
        show={showUserModal}
        isFullScreen={false}
        zPosition={ZPosition.Top}
        onSuccess={handleUserSuccess}
        onClose={handleUserClose}
      />

      <Dialog
        open={openDialog}
        title="Brisanje korisnika"
        message="Korisnik se briše iz sustava, ne samo iz ovog tenanta. Više se neće moći prijaviti."
        confirmText="Obriši"
        cancelText="Odustani"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </div>
  );
};

export default memo(TenantUserStep);
